import { computed, ref } from 'vue';
import store from '@/store';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import type { ListElements } from '@/typings/customilyElements/layer';
import type { CanvasElement } from '@/typings/customilyElements/canvasElements';

export type MoveEvent = {
    draggedContext: {
        element: ListElements;
    };
    relatedContext: {
        element: ListElements;
    };
};

// Define an interface for validation rules
export interface ValidationRule {
    name: string;
    validate: (event: MoveEvent) => boolean;
    enabled?: boolean;
}

export const useLayersDraggable = () => {
    const canvas = computed(() => store.state.storeLayers?.canvas);
    const flags = computed(() => store.state.storeLayers?.flags);
    const template = computed(() => store.state.storeLayers?.template);
    const pageEpsElements = computed(() => store.state.storeLayers?.rawElements.pageEpsElements);
    const currentDraggedItem = computed(() => store.state.storeLayers?.currentDraggedItem);

    // Array that will store all validation rules
    const validationRules = ref<ValidationRule[]>([]);

    const dragOptions = computed(() => ({
        animation: 150,
        ghostClass: 'ghost',
        handle: '.draggable',
        forceFallback: true,
        scrollSensitivity: 290,
        delay: 5,
        disabled: flags.value?.readonly,
        filter: '.no-drag, input, button, .locked',
        sort: true,
        preventOnFilter: false,
        tag: 'div'
    }));

    const checkIsElementInEpsList = (
        item: ListElements,
        listEPS?: {
            imagePlaceHoldersEps: CanvasElement[];
            textsEps: CanvasElement[];
            vectorEPSEps: CanvasElement[];
            circularTextsEps: CanvasElement[];
        }[]
    ) => {
        const elementUuid = item.element?.uuid;

        if (!listEPS || !elementUuid) return false;

        return listEPS.some((eps) => {
            // Check if element UUID exists in any of the EPS arrays
            const arrayChecks = [
                { arr: eps.circularTextsEps, field: 'uuid' },
                { arr: eps.textsEps, field: 'uuid' },
                { arr: eps.imagePlaceHoldersEps, field: 'uuid' },
                { arr: eps.vectorEPSEps, field: 'uuid' }
            ];

            return arrayChecks.some(
                ({ arr, field }) =>
                    Array.isArray(arr) && arr.some((item) => item[field as keyof typeof item] === elementUuid)
            );
        });
    };

    const isPreviewStep = computed(() => {
        return flags.value?.isPreviewStep;
    });

    // Check if an element has nested groups
    const hasNestedGroups = (element: ListElements): boolean => {
        if (!element.children || element.children.length === 0) {
            return false;
        }

        return element.children.some(
            (child) =>
                child.type === ListElementType.Group ||
                (child.children && child.children.some((grandchild) => grandchild.type === ListElementType.Group))
        );
    };

    const tryingMoveBelowSolidBg = (targetElementType: string): boolean => {
        if (targetElementType !== 'eps') return true;
        const object = store.state.storeLayers?.rawElements.canvasElements?.find((o) => o.zIndex === 1);
        return !(object?.type === 'vector-placeholder' && object?.isSolidBackground);
    };

    // Function to register new rules
    const registerRule = (rule: ValidationRule) => {
        // If a rule with the same name already exists, replace it
        const existingRuleIndex = validationRules.value.findIndex((r) => r.name === rule.name);
        if (existingRuleIndex >= 0) {
            validationRules.value[existingRuleIndex] = rule;
        } else {
            validationRules.value.push(rule);
        }
    };

    // Rule: Validate group movement
    registerRule({
        name: 'validatePreviewGroupsMovement',
        validate: (event: MoveEvent) => {
            const draggedItem = event.draggedContext.element;
            const targetItem = event.relatedContext.element;

            if (!draggedItem || !targetItem) {
                return true;
            }

            if (draggedItem?.type !== ListElementType.Group) {
                return true;
            }

            if (!isPreviewStep.value) {
                return true;
            }

            if (draggedItem.parent) {
                // Make sure it can't be moved outside its container
                // Check if trying to move the element outside its current parent
                const draggedParentUuid = draggedItem.parent?.element?.uuid;
                const targetParentUuid = targetItem.parent?.element?.uuid;

                // Only allow movements within the same parent
                return targetParentUuid === draggedParentUuid;
            }

            return true;
        }
    });

    // Rule: Allow movement of root EPS elements between EPS only
    registerRule({
        name: 'validateRootEpsMovement',
        validate: (event: MoveEvent) => {
            const draggedElement = event.draggedContext.element;
            const targetElement = event.relatedContext.element;

            if (!draggedElement || !targetElement) {
                return true;
            }

            // Allow moving PageEps between other PageEps (never nest or insert into other types)
            if (draggedElement.type === ListElementType.PageEps) {
                // Only allow if the target has no parent
                if (!targetElement.parent) {
                    return true;
                }
                return false;
            }

            return true;
        }
    });

    registerRule({
        name: 'blockRootEpsItemsMovement',
        validate: (event: MoveEvent) => {
            if (isPreviewStep.value) {
                return true;
            }

            const draggedElement = event.draggedContext.element;
            const targetElement = event.relatedContext.element;

            if (
                [ListElementType.Group, ListElementType.CanvasElement].includes(draggedElement?.type) &&
                targetElement?.type === ListElementType.PageEps
            ) {
                return false;
            }

            return true;
        }
    });

    // Rule: Block movement of EPS elements in preview mode
    registerRule({
        name: 'blockEpsElementsInPreview',
        validate: (event: MoveEvent) => {
            if (!isPreviewStep.value) {
                return true;
            }

            const draggedItem = event.draggedContext.element;
            const targetItem = event.relatedContext.element;

            // Check if the element is in the EPS list
            const isElementInEpsList = checkIsElementInEpsList(draggedItem, template.value?.listEPS);

            // If the element is not in the EPS list, allow movement
            if (!isElementInEpsList) {
                return true;
            }

            // Check if the dragged item has a parent
            if (draggedItem.parent) {
                // Make sure it can't be moved outside its container
                // Check if trying to move the element outside its current parent
                const draggedParentUuid = draggedItem.parent?.element?.uuid;
                const targetParentUuid = targetItem.parent?.element?.uuid;

                // Only allow movements within the same parent
                return targetParentUuid === draggedParentUuid;
            }

            return true;
        }
    });

    // Rule: Keep child elements within EPS parents in preview mode
    registerRule({
        name: 'keepChildrenWithinEpsParents',
        validate: (event: MoveEvent) => {
            if (!isPreviewStep.value) {
                return true;
            }

            const draggedItem = event.draggedContext.element;
            const targetItem = event.relatedContext.element;

            // If the parent element is in the EPS list,
            // don't allow removing it from its parent
            if (draggedItem.parent && checkIsElementInEpsList(draggedItem.parent, template.value?.listEPS)) {
                // Check if trying to move the element outside its current parent
                const targetParentUuid = targetItem.parent?.element?.uuid;
                const currentParentUuid = draggedItem.parent.element?.uuid;

                // If the movement target is not the same parent or a child of the same parent,
                // we're trying to remove the element from its parent, so we block it
                if (targetParentUuid !== currentParentUuid && targetItem.element?.uuid !== currentParentUuid) {
                    return false;
                }
            }

            return true;
        }
    });

    // Rule: Don't allow moving elements associated with text mask in step 2
    registerRule({
        name: 'textMaskOnStep2',
        validate: (event: MoveEvent) => {
            if (!isPreviewStep.value) {
                return true;
            }

            const draggedItem = event.draggedContext.element;
            if (
                draggedItem &&
                ((draggedItem.element.type === 'bounded-text' && draggedItem.element.useAsMask) ||
                    (draggedItem.element.type === 'image-placeholder' && draggedItem.element.textMaskUuid))
            ) {
                return false;
            }

            return true;
        }
    });

    registerRule({
        name: 'keepSolidBackgroundZIndex',
        validate: (event: MoveEvent): boolean => {
            const draggedItem = event.draggedContext.element;
            const targetItem = event.relatedContext.element;
            if (!targetItem) {
                return true;
            }

            const isSolidBg = (item: ListElements) =>
                item?.element?.type === 'vector-placeholder' && item?.element?.isSolidBackground;

            return (
                isPreviewStep.value ||
                (!isSolidBg(draggedItem) && !isSolidBg(targetItem) && tryingMoveBelowSolidBg(targetItem.element.type))
            );
        }
    });

    // Handle move event with the new rules system
    const handleMove = (event: MoveEvent) => {
        const draggedItem = event.draggedContext.element;
        const targetItem = event.relatedContext.element;
        store.dispatch('storeLayers/setDraggedItem', {
            currentDraggedItem: draggedItem,
            currentTargetItem: targetItem
        });

        return validationRules.value.every((rule) => rule.validate(event));
    };

    // Get unique key for an element
    const getItemKey = (item: ListElements) => {
        return `${canvas.value?._selectedPreview?.PreviewId}-${item.type}-${item.element.type}-${item.element.uuid}-${item.element.id}`;
    };

    // Handle change in tree elements
    const handleChange = () => {
        const updatedTreeItems = store.state.storeLayers?.treeItems;
        store.dispatch('storeLayers/updateElements', updatedTreeItems);
    };

    return {
        currentDraggedItem,
        flags,
        pageEpsElements,
        dragOptions,
        isPreviewStep,
        hasNestedGroups,
        registerRule,
        validationRules,
        handleMove,
        getItemKey,
        handleChange
    };
};
